import React from "react";
import { Badge, Button, Tooltip } from "@mui/material";
import { styled, SxProps, Theme } from "@mui/material/styles";
import { TooltipProps } from "@mui/material/Tooltip";
import { AddShoppingCart } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";

type CartButtonProps = {
  onClick: (() => void) | undefined;
  minWidth?: string | number;
  sx?: SxProps<Theme>;
  tooltipPlacement?: TooltipProps["placement"];
  children?: React.ReactNode;
};

/** ── Constants ───────────────────────────────────────────────────────────── */
const EMPTY_CART_TEXT = "El carrito de compras está vacío";
const VIEW_CART_TEXT = "Ver carrito de compras";

/** ── Styled Components ───────────────────────────────────────────────────── */
const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold ?? 700,
    cursor: "pointer",
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.getContrastText(theme.palette.grey[500]),
  },
  "&.nonEmpty .MuiBadge-badge": {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.getContrastText(theme.palette.warning.main),
  },
}));

const StyledCartButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
  },
}));

/** ── Component ───────────────────────────────────────────────────────────── */
const CartButton: React.FC<CartButtonProps> = ({
  onClick,
  sx,
  tooltipPlacement = "top-end",
  children,
}) => {
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const cartItemsCount = cartItems.reduce((total, item) => total + item.quantity, 0);

  const tooltipContent = cartItemsCount > 0 ? VIEW_CART_TEXT : EMPTY_CART_TEXT;

  return (
    <Tooltip title={tooltipContent} placement={tooltipPlacement}>
      <StyledCartButton
        variant="outlined"
        color={"success"}
        sx={{ borderRadius: 5, fontWeight: "bold", bgcolor: "white", boxShadow: 5, ...sx }}
        onClick={onClick}
      >
        <StyledBadge
          className={cartItemsCount > 0 ? "nonEmpty" : undefined}
          badgeContent={cartItemsCount}
        >
          {children}
          <AddShoppingCart sx={{ cursor: "pointer" }} />
        </StyledBadge>
      </StyledCartButton>
    </Tooltip>
  );
};

export default CartButton;
